/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import request from '@/utils/request';

export interface UcAgentControl {
  name: string;
  label: string;
  url: string;
}
export interface UcAgent {
  enabled: boolean;
  agent_info: {
    name: string;
    icon: string;
    url: string;
    display_name: string;
    login_redirect_url: string;
    sign_up_redirect_url: string;
    control_center: UcAgentControl[];
    enabled_original_user_system: boolean;
  };
}

export interface UcSettingAgent {
  enabled: boolean;
  redirect_url: string;
}
export interface UcSettings {
  profile_setting_agent: UcSettingAgent;
  account_setting_agent: UcSettingAgent;
}

export interface UcLoginSync {
  access_token?: string;
  err_title?: string;
  err_msg?: string;
}

export interface UcBrandingEntry {
  icon: string;
  name: string;
  label: string;
  url: string;
}
export interface UcBranding {
  enabled: boolean;
  personal_branding: UcBrandingEntry[];
}

export interface AdminUcAgent {
  allow_create_user: boolean;
  allow_update_user_status: boolean;
  allow_update_user_password: boolean;
  allow_update_user_role: boolean;
}

export const getUcAgent = () => {
  const apiUrl = `/answer/api/v1/user-center/agent`;
  return request.get<UcAgent>(apiUrl);
};

const getNewApiUserID = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  try {
    const uid = window.localStorage.getItem('uid');
    if (uid) {
      return uid;
    }
    const rawUser = window.localStorage.getItem('user');
    if (!rawUser) {
      return '';
    }
    const user = JSON.parse(rawUser);
    return user?.id ? String(user.id) : '';
  } catch {
    return '';
  }
};

export const syncUcLogin = async () => {
  const apiBase = process.env.REACT_APP_API_URL || '';
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  const newApiUserID = getNewApiUserID();
  if (newApiUserID) {
    headers['New-Api-User'] = newApiUserID;
  }
  const res = await fetch(`${apiBase}/answer/api/v1/user-center/login/sync`, {
    credentials: 'include',
    headers,
  });
  if (!res.ok) {
    return null;
  }
  const body = await res.json().catch(() => null);
  return (body?.data || null) as UcLoginSync | null;
};

export const getAdminUcAgent = () => {
  const apiUrl = `/answer/admin/api/user-center/agent`;
  return request.get<AdminUcAgent>(apiUrl);
};
export const getUcSettings = () => {
  const apiUrl = `/answer/api/v1/user-center/user/settings`;
  return request.get<UcSettings>(apiUrl);
};

export const getUcBranding = (username: string) => {
  const apiUrl = `/answer/api/v1/user-center/personal/branding?username=${username}`;
  return request.get<UcBranding>(apiUrl);
};
